// ═══════════════════════════════════════════════════════════════
// COMPONENTE: Detalles del Contrato
// ═══════════════════════════════════════════════════════════════

import React from 'react';
import {
  ArrowLeft,
  Edit,
  Trash2,
  FileText,
  User,
  Calendar,
  DollarSign,
  Briefcase,
  Building2,
  Clock,
  AlertCircle,
  Download,
  CheckCircle,
  XCircle,
  History
} from 'lucide-react';
import { getEmployeePhotoUrl } from '../../../utils/imageUtils';
import SalaryHistoryTable from '../SalaryHistory/SalaryHistoryTable';

const ContractDetails = ({ contract, onClose, onEdit, onDelete }) => {
  const getFotoUrl = getEmployeePhotoUrl;

  const getTipoLabel = (tipo) => {
    const labels = {
      indefinido: 'Indefinido',
      plazo_fijo: 'Plazo Fijo',
      temporal: 'Temporal',
      por_obra: 'Por Obra',
      prueba: 'Período de Prueba'
    };
    return labels[tipo] || tipo;
  };

  const getEstadoConfig = (estado) => {
    const configs = {
      activo: { bg: 'bg-green-100', text: 'text-green-700', label: 'Activo', icon: CheckCircle },
      finalizado: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Finalizado', icon: Clock },
      rescindido: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rescindido', icon: XCircle },
      suspendido: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Suspendido', icon: AlertCircle }
    };
    return configs[estado] || configs.activo;
  };

  const estadoConfig = getEstadoConfig(contract.estado);
  const EstadoIcon = estadoConfig.icon;

  const diasRestantes = contract.dias_restantes;
  const estaProximoVencer = diasRestantes !== null && diasRestantes <= 30 && diasRestantes >= 0;

  return (
    <div className="space-y-4">

      {/* ── Back button ──────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <button
          onClick={onClose}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
        >
          <ArrowLeft size={16} />
          Volver a Contratos
        </button>
      </div>

      {/* ── Contract card header ──────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Avatar */}
            {getEmployeePhotoUrl(contract.empleado_foto) ? (
              <img
                src={getEmployeePhotoUrl(contract.empleado_foto)}
                alt={contract.empleado_nombre}
                className="w-14 h-14 rounded-full object-cover border-2 border-gray-200"
                onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
              />
            ) : null}
            <div
              className="w-14 h-14 rounded-full bg-blue-600 flex items-center justify-center text-white text-xl font-bold flex-shrink-0"
              style={{ display: getEmployeePhotoUrl(contract.empleado_foto) ? 'none' : 'flex' }}
            >
              {contract.empleado_nombre?.[0]?.toUpperCase()}
            </div>

            <div>
              <h2 className="text-xl font-bold text-gray-900">{contract.empleado_nombre}</h2>
              <p className="text-sm text-gray-400">{contract.empleado_codigo}</p>
              <div className="flex items-center gap-2 mt-1.5">
                <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded ${estadoConfig.bg} ${estadoConfig.text}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    contract.estado === 'activo' ? 'bg-green-500' :
                    contract.estado === 'rescindido' ? 'bg-red-500' :
                    contract.estado === 'suspendido' ? 'bg-amber-500' : 'bg-gray-400'
                  }`} />
                  {estadoConfig.label}
                </span>
                {contract.numero_contrato && (
                  <span className="text-xs font-mono text-blue-600">{contract.numero_contrato}</span>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          {contract.estado === 'activo' && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => onEdit(contract)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Edit size={14} />
                Editar
              </button>
              <button
                onClick={() => onDelete(contract)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
              >
                <Trash2 size={14} />
                Rescindir
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Alerta de vencimiento */}
      {estaProximoVencer && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
          <div>
            <p className="font-semibold text-amber-900">
              Este contrato vence en {diasRestantes} días
            </p>
            <p className="text-sm text-amber-700">
              Fecha de vencimiento: {new Date(contract.fecha_fin + 'T12:00:00').toLocaleDateString('es-DO', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Columna Principal */}
        <div className="lg:col-span-2 space-y-6">
          {/* Información del Empleado */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Información del Empleado
            </h2>
            <div className="flex items-center gap-4">
              {getFotoUrl(contract.empleado_foto) ? (
                <img
                  src={getFotoUrl(contract.empleado_foto)}
                  alt={contract.empleado_nombre}
                  className="w-20 h-20 rounded-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div
                className="w-20 h-20 rounded-full bg-blue-600 flex items-center justify-center text-white text-2xl font-bold"
                style={{ display: getFotoUrl(contract.empleado_foto) ? 'none' : 'flex' }}
              >
                {contract.empleado_nombre?.[0]?.toUpperCase()}
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">{contract.empleado_nombre}</h3>
                <p className="text-sm text-gray-500">Código: {contract.empleado_codigo}</p>
              </div>
            </div>
          </div>

          {/* Detalles del Contrato */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Detalles del Contrato
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-500 mb-1">Tipo de Contrato</p>
                <p className="font-semibold text-gray-900">{getTipoLabel(contract.tipo_contrato)}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-1">Número de Contrato</p>
                <p className="font-semibold text-gray-900">{contract.numero_contrato || 'N/A'}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-1">Fecha de Inicio</p>
                <p className="font-semibold text-gray-900 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  {new Date(contract.fecha_inicio + 'T12:00:00').toLocaleDateString('es-DO', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-1">Fecha de Fin</p>
                <p className="font-semibold text-gray-900 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  {contract.fecha_fin ?
                    new Date(contract.fecha_fin + 'T12:00:00').toLocaleDateString('es-DO', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) :
                    'Indefinido'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Puesto y Remuneración */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-blue-600" />
              Puesto y Remuneración
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-500 mb-1">Cargo</p>
                <p className="font-semibold text-gray-900 flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-gray-400" />
                  {contract.cargo || 'N/A'}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-1">Departamento</p>
                <p className="font-semibold text-gray-900 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  {contract.departamento || 'N/A'}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-1">Salario</p>
                <p className="font-bold text-xl text-blue-600 flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  {contract.moneda || 'RD$'} {parseFloat(contract.salario || 0).toLocaleString('es-DO', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-1">Jornada Laboral</p>
                <p className="font-semibold text-gray-900 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  {contract.tipo_jornada === 'completa' ? 'Tiempo Completo' :
                   contract.tipo_jornada === 'parcial' ? 'Tiempo Parcial' :
                   'Por Horas'} ({contract.horas_semanales || 0} hrs/semana)
                </p>
              </div>
            </div>
          </div>

          {/* Historial de Salarios */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <History className="w-5 h-5 text-blue-600" />
              Historial de Salarios
            </h2>
            <SalaryHistoryTable employeeId={contract.employee_id} />
          </div>

          {/* Período de Prueba */}
          {contract.tiene_periodo_prueba && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-blue-600" />
                Período de Prueba
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Duración</p>
                  <p className="font-semibold text-gray-900">
                    {contract.duracion_prueba_dias} días
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500 mb-1">Fecha de Fin</p>
                  <p className="font-semibold text-gray-900">
                    {contract.fecha_fin_prueba ?
                      new Date(contract.fecha_fin_prueba + 'T12:00:00').toLocaleDateString('es-DO') :
                      'N/A'
                    }
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Notas */}
          {contract.notas && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Notas Adicionales</h2>
              <p className="text-gray-700 whitespace-pre-wrap">{contract.notas}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Estado */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-sm font-semibold text-gray-500 mb-3">Estado del Contrato</h3>
            <div className={`flex items-center gap-3 px-4 py-3 rounded-lg ${estadoConfig.bg}`}>
              <EstadoIcon className={`w-6 h-6 ${estadoConfig.text}`} />
              <span className={`font-bold ${estadoConfig.text}`}>{estadoConfig.label}</span>
            </div>
          </div>

          {/* Renovación */}
          {contract.es_renovacion && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-500 mb-3">Renovación</h3>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  Este es el contrato <span className="font-bold">#{contract.numero_renovacion}</span> del empleado
                </p>
              </div>
            </div>
          )}

          {/* Documento */}
          {contract.documento_url && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-500 mb-3">Documento del Contrato</h3>
              <a
                href={contract.documento_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all"
              >
                <Download className="w-5 h-5" />
                <span className="font-semibold">Descargar Contrato</span>
              </a>
            </div>
          )}

          {/* Auditoría */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-sm font-semibold text-gray-500 mb-3">Auditoría</h3>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-gray-500">Creado</p>
                <p className="font-semibold text-gray-900">
                  {new Date(contract.created_at).toLocaleString('es-DO')}
                </p>
              </div>
              {contract.updated_at && contract.updated_at !== contract.created_at && (
                <div>
                  <p className="text-gray-500">Última Actualización</p>
                  <p className="font-semibold text-gray-900">
                    {new Date(contract.updated_at).toLocaleString('es-DO')}
                  </p>
                </div>
              )}
              {contract.fecha_finalizacion && (
                <div>
                  <p className="text-gray-500">Fecha de Finalización</p>
                  <p className="font-semibold text-gray-900">
                    {new Date(contract.fecha_finalizacion + 'T12:00:00').toLocaleDateString('es-DO')}
                  </p>
                </div>
              )}
              {contract.motivo_finalizacion && (
                <div>
                  <p className="text-gray-500">Motivo de Finalización</p>
                  <p className="font-semibold text-gray-900">{contract.motivo_finalizacion}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractDetails;
